import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StudentAvatar } from "@/components/StudentAvatar";
import { RemoveFaceButton } from "@/components/RemoveFaceButton";
import { TransferClassButton } from "@/components/TransferClassButton";
import { StudentActions } from "@/components/StudentActions";
import { useAuth } from "@/context/AuthContext";
import { MeetingList } from "@/pages/Reunioes";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, Loader2, IdCard, CalendarDays, MapPin, Phone, Mail, MessageCircle,
  BookOpen, Percent, ClipboardList, ShieldAlert, FileText, Award, History, Clock, Star,
} from "lucide-react";

const TABS = [
  { v: "overview", label: "Visão Geral", icon: IdCard, testid: "student-profile-tab-overview" },
  { v: "academic", label: "Acadêmico", icon: BookOpen, testid: "student-profile-tab-academic", phase: "Fase 2" },
  { v: "attendance", label: "Frequência", icon: Percent, testid: "student-profile-tab-attendance", phase: "Fase 2" },
  { v: "pedagogical", label: "Pedagógico", icon: ClipboardList, testid: "student-profile-tab-pedagogical", phase: "Fase 2" },
  { v: "disciplinary", label: "Disciplinar", icon: ShieldAlert, testid: "student-profile-tab-disciplinary", phase: "Fase 4" },
  { v: "documents", label: "Documentos", icon: FileText, testid: "student-profile-tab-documents", phase: "Fase 4" },
  { v: "achievements", label: "Conquistas", icon: Award, testid: "student-profile-tab-achievements", phase: "Fase 5" },
  { v: "history", label: "Linha do Tempo", icon: History, testid: "student-profile-tab-history" },
];

function Placeholder({ icon: Icon, phase, text }) {
  return (
    <Card className="flex flex-col items-center justify-center border-dashed border-slate-300 py-16 text-center">
      <Icon className="mb-3 h-10 w-10 text-slate-300" />
      <p className="font-display text-lg font-semibold text-slate-600">{text}</p>
      <p className="mt-1 text-sm text-slate-400">Este módulo será implementado na <span className="font-semibold text-orange-600">{phase}</span>.</p>
    </Card>
  );
}

export default function StudentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  const { user } = useAuth();
  const gestao = ["SUPERADMIN", "DIRECAO", "COORDENACAO"].includes(user?.role);

  const reload = () => api.get(`/students/${id}`).then((r) => setS(r.data)).catch(() => setErr(true));
  useEffect(() => { reload(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div className="py-16 text-center text-slate-500">Estudante não encontrado. <Button variant="link" onClick={() => navigate("/alunos")}>Voltar</Button></div>;
  if (!s) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>;

  const primary = (s.guardians || []).find((g) => g.is_primary) || (s.guardians || [])[0];
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => navigate("/alunos")} className="mb-4 -ml-2 text-slate-500"><ArrowLeft className="mr-1 h-4 w-4" /> Estudantes</Button>

      {/* Header */}
      <Card className="border-slate-200 p-5 sm:p-6" data-testid="student-profile-header">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <StudentAvatar student={s} className="h-20 w-20 border-2 border-white shadow-md ring-2 ring-blue-100" fallbackClassName="bg-blue-700 text-2xl font-bold text-white" />
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold text-slate-900">{s.full_name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 font-mono text-xs font-semibold text-slate-600"><IdCard className="h-3 w-3" /> {s.registration}</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700">{s.class_name || "Sem turma"}</span>
              {s.academic_year && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">{s.academic_year}</span>}
              <StatusBadge status={s.enrollment_status} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <StudentActions student={s} onDone={reload} />
            <TransferClassButton student={s} onDone={reload} />
            <RemoveFaceButton student={s} onDone={reload} />
          </div>
        </div>
      </Card>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Tabs */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="overview">
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1">
              {TABS.map((t) => (
                <TabsTrigger key={t.v} value={t.v} data-testid={t.testid} className="gap-1.5 text-xs data-[state=active]:bg-white">
                  <t.icon className="h-4 w-4" /> <span className="hidden sm:inline">{t.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-4">
              <Card className="border-slate-200 p-5">
                <h3 className="mb-3 font-display font-semibold text-slate-800">Dados do Estudante</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Info icon={CalendarDays} label="Nascimento" value={fmtDate(s.birth_date)} />
                  <Info icon={BookOpen} label="Turma" value={s.class_name || "—"} />
                  <Info icon={IdCard} label="Matrícula" value={s.registration} />
                  <Info icon={MapPin} label="Endereço" value={s.address || "—"} />
                  <Info icon={BookOpen} label="Curso" value={s.course || "—"} />
                  <Info icon={MapPin} label="Nacionalidade / Naturalidade" value={[s.nationality, [s.birth_city, s.birth_state].filter(Boolean).join("/")].filter(Boolean).join(" • ") || "—"} />
                  <Info icon={IdCard} label="Filiação" value={[s.father_name && `Pai: ${s.father_name}`, s.mother_name && `Mãe: ${s.mother_name}`].filter(Boolean).join(" • ") || "—"} />
                  <Info icon={IdCard} label="Identidade / CPF" value={[s.rg && `RG ${s.rg}${s.rg_issuer ? ` (${s.rg_issuer}${s.rg_issue_date ? `, ${fmtDate(s.rg_issue_date)}` : ""})` : ""}`, s.cpf && `CPF ${s.cpf}`].filter(Boolean).join(" • ") || "—"} />
                </div>
              </Card>
              <Card className="border-slate-200 p-5">
                <h3 className="mb-3 font-display font-semibold text-slate-800">Responsáveis</h3>
                {(s.guardians || []).length === 0 ? <p className="text-sm text-slate-400">Nenhum responsável cadastrado.</p> : (
                  <div className="space-y-3">
                    {s.guardians.map((g, i) => (
                      <div key={i} className="rounded-xl border border-slate-200 p-3" data-testid={`guardian-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">{g.name}</span>
                          <span className="text-xs text-slate-400">({g.relationship || "—"})</span>
                          {g.is_primary && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">PRINCIPAL</span>}
                          {g.is_legal && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">LEGAL</span>}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                          {g.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {g.phone}</span>}
                          {g.whatsapp && <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {g.whatsapp}</span>}
                          {g.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {g.email}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="academic" className="mt-4"><AcademicTab academic={s.academic} /></TabsContent>
            <TabsContent value="attendance" className="mt-4"><AttendanceTab frequency={s.frequency} imported={s.imported_absences} /></TabsContent>
            <TabsContent value="pedagogical" className="mt-4"><PedagogicalTab notes={s.pedagogical_notes} /></TabsContent>
            <TabsContent value="disciplinary" className="mt-4"><DisciplinaryTab occurrences={s.occurrences} /></TabsContent>
            <TabsContent value="documents" className="mt-4 space-y-4"><DocumentsTab documents={s.documents} />
              {gestao && <Card className="border-slate-200 p-5" data-testid="student-meetings"><h3 className="mb-2 font-display font-semibold text-slate-800">Reuniões e atendimentos</h3><MeetingList studentId={s.id} compact /></Card>}
            </TabsContent>
            <TabsContent value="achievements" className="mt-4"><AchievementsTab achievements={s.achievements} excellence={s.excellence} /></TabsContent>

            <TabsContent value="history" className="mt-4">
              <Card className="border-slate-200 p-5">
                <h3 className="mb-4 font-display font-semibold text-slate-800">Linha do Tempo</h3>
                <Timeline events={s.timeline} />
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right timeline summary */}
        <div>
          <Card className="border-slate-200 p-5">
            <div className="mb-4 flex items-center gap-2"><Clock className="h-5 w-5 text-blue-700" /><h3 className="font-display font-semibold text-slate-800">Eventos Recentes</h3></div>
            <Timeline events={s.timeline} compact />
          </Card>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <div><div className="text-xs text-slate-400">{label}</div><div className="text-sm font-medium text-slate-700">{value}</div></div>
    </div>
  );
}

function gradeColor(v, passing) {
  if (v === null || v === undefined) return "text-slate-300";
  return v >= passing ? "text-green-700" : "text-red-600";
}

function AcademicTab({ academic }) {
  if (!academic || !academic.subjects?.length)
    return <Placeholder icon={BookOpen} phase="Fase 2" text="Sem dados acadêmicos" />;
  const passing = academic.passing_grade ?? 5;
  const terms = Array.from({ length: academic.num_terms || 3 }, (_, i) => i + 1);
  return (
    <Card className="overflow-x-auto border-slate-200 p-0">
      <div className="border-b border-slate-100 p-4"><h3 className="font-display font-semibold text-slate-800">Desempenho por Disciplina</h3><p className="text-xs text-slate-400">Médias finais do trimestre (nota de corte {passing})</p></div>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
          <th className="p-3">Disciplina</th>{terms.map((t) => <th key={t} className="p-3 text-center">{t}º Tri</th>)}
        </tr></thead>
        <tbody>
          {academic.subjects.map((s) => (
            <tr key={s.subject_id} className="border-b border-slate-50">
              <td className="p-3 font-medium text-slate-700">{s.subject}</td>
              {terms.map((t) => {
                const tr = s.terms.find((x) => x.term === t);
                const v = tr?.final;
                return <td key={t} className={`p-3 text-center font-bold ${gradeColor(v, passing)}`}>{v ?? "—"}{tr?.recovery != null && <span className="ml-1 text-[10px] font-normal text-orange-500">(NOA)</span>}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function AttendanceTab({ frequency, imported }) {
  const impBlock = imported && imported.length ? (
    <Card className="border-slate-200 p-5" data-testid="imported-absences">
      <h3 className="mb-3 font-display font-semibold text-slate-800">Faltas importadas (por disciplina/trimestre)</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400"><th className="p-2">Disciplina</th><th className="p-2 text-center">Trimestre</th><th className="p-2 text-center">Faltas</th></tr></thead>
          <tbody>{imported.map((a, i) => (<tr key={i} className="border-b border-slate-50"><td className="p-2 text-slate-700">{a.subject_name || "—"}</td><td className="p-2 text-center">{a.term_order}º</td><td className="p-2 text-center font-bold text-red-600">{a.faltas}</td></tr>))}</tbody>
        </table>
      </div>
    </Card>
  ) : null;
  if (!frequency || !frequency.per_subject?.length)
    return impBlock || <Placeholder icon={Percent} phase="Fase 2" text="Sem dados de frequência" />;
  return (
    <div className="space-y-4">
      {impBlock}
      <Card className="flex items-center justify-between border-slate-200 p-5">
        <div><div className="text-xs font-bold uppercase tracking-wider text-slate-400">Frequência Geral</div><div className="font-display text-4xl font-black text-slate-900">{frequency.overall ?? "—"}%</div></div>
        <div className={`rounded-xl px-4 py-2 text-sm font-semibold ${(frequency.overall ?? 0) >= 75 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{(frequency.overall ?? 0) >= 75 ? "Adequada" : "Atenção"}</div>
      </Card>
      <Card className="overflow-x-auto border-slate-200 p-0">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400"><th className="p-3">Disciplina</th><th className="p-3 text-center">Aulas</th><th className="p-3 text-center">Presenças</th><th className="p-3 text-center">Faltas</th><th className="p-3 text-center">F. Just.</th><th className="p-3 text-center">%</th></tr></thead>
          <tbody>
            {frequency.per_subject.map((r, i) => (
              <tr key={i} className="border-b border-slate-50">
                <td className="p-3 font-medium text-slate-700">{r.subject}</td>
                <td className="p-3 text-center">{r.aulas}</td><td className="p-3 text-center">{r.presencas}</td>
                <td className="p-3 text-center">{r.faltas}</td><td className="p-3 text-center">{r.faltas_justificadas}</td>
                <td className={`p-3 text-center font-bold ${(r.percentual ?? 0) >= 75 ? "text-green-700" : "text-red-600"}`}>{r.percentual ?? "—"}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function PedagogicalTab({ notes }) {
  if (!notes || !notes.length)
    return <Placeholder icon={ClipboardList} phase="Fase 2" text="Nenhuma observação pedagógica" />;
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700">Observações pedagógicas não são ocorrências disciplinares.</div>
      {notes.map((n) => (
        <Card key={n.id} className="border-slate-200 p-4">
          <div className="flex items-center justify-between"><span className="text-xs text-slate-400">{n.date ? new Date(n.date).toLocaleDateString("pt-BR") : ""}</span>{n.author && <span className="text-xs text-slate-400">{n.author}</span>}</div>
          <p className="mt-1.5 text-sm text-slate-600">{n.text}</p>
        </Card>
      ))}
    </div>
  );
}

function AchievementsTab({ achievements, excellence }) {
  const terms = (achievements || []).filter((a) => a.type === "EXCELENCIA").length;
  const cat = terms >= 3 ? { label: "OURO", cls: "from-amber-400 to-yellow-500" }
    : terms === 2 ? { label: "PRATA", cls: "from-slate-300 to-slate-400" }
    : terms === 1 ? { label: "BRONZE", cls: "from-orange-400 to-orange-600" } : null;
  return (
    <div className="space-y-4">
      {cat && (
        <Card className={`border-0 bg-gradient-to-br ${cat.cls} p-5 text-white`}>
          <div className="flex items-center gap-3"><Star className="h-8 w-8 fill-white/90 text-white" />
            <div><div className="font-display text-2xl font-black">Excelência {cat.label}</div>
              <div className="text-sm text-white/90">{terms} trimestre(s) em Excelência Acadêmica</div></div>
          </div>
        </Card>
      )}
      {(!achievements || achievements.length === 0) ? (
        <Placeholder icon={Award} phase="Fase 5" text="Nenhuma conquista ainda" />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {achievements.map((a) => (
            <Card key={a.id} className="flex items-center gap-3 border-slate-200 p-4" data-testid={`achievement-${a.id}`}>
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-orange-100 text-orange-600"><Award className="h-6 w-6" /></div>
              <div><div className="font-display font-bold text-slate-800">🏆 Excelência Acadêmica</div>
                <div className="text-xs text-slate-500">{a.label}</div></div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DisciplinaryTab({ occurrences }) {
  if (!occurrences || !occurrences.length)
    return <Placeholder icon={ShieldAlert} phase="Fase 4" text="Nenhuma ocorrência disciplinar" />;
  const ST = { REGISTRADA: "bg-blue-100 text-blue-700", AGUARDANDO_ASSINATURA: "bg-amber-100 text-amber-700", DOCUMENTO_ASSINADO: "bg-green-100 text-green-700" };
  return (
    <div className="space-y-3">
      {occurrences.map((o) => (
        <Card key={o.id} className="border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">{o.date_fact}{o.time_fact ? ` · ${o.time_fact}` : ""}</span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ST[o.status] || "bg-slate-100 text-slate-500"}`}>{o.status?.replace(/_/g, " ")}</span>
          </div>
          <p className="mt-1.5 text-sm text-slate-700">{o.description}</p>
          <p className="mt-1 text-xs text-slate-400">Registrado por {o.reporter || "—"}{o.origin === "VOZ" ? " · via voz+IA" : ""}</p>
        </Card>
      ))}
    </div>
  );
}

function DocumentsTab({ documents }) {
  const download = async (id) => {
    try { const { data } = await api.get(`/disciplinar/files/${id}`, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); }
    catch { /* noop */ }
  };
  if (!documents || !documents.length)
    return <Placeholder icon={FileText} phase="Fase 4" text="Nenhum documento assinado" />;
  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-400">Apenas documentos assinados e anexados aparecem aqui (o PDF provisório não é definitivo).</p>
      {documents.map((d) => (
        <Card key={d.id} className="flex items-center justify-between border-slate-200 p-3">
          <div className="flex items-center gap-2"><FileText className="h-5 w-5 text-green-600" /><span className="text-sm font-medium text-slate-700">{d.original_filename || "Documento assinado"}</span></div>
          <button onClick={() => download(d.id)} className="text-sm font-semibold text-blue-700 hover:underline" data-testid={`profile-doc-${d.id}`}>Abrir</button>
        </Card>
      ))}
    </div>
  );
}

function Timeline({ events, compact }) {
  if (!events || events.length === 0) return <p className="text-sm text-slate-400">Nenhum evento registrado ainda.</p>;
  return (
    <div className="relative space-y-4 pl-5">
      <div className="absolute left-[7px] top-1 h-full w-px bg-slate-200" />
      {events.map((e) => (
        <div key={e.id} className="relative" data-testid="student-timeline-item">
          <div className="absolute -left-5 top-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-orange-500 shadow" />
          <div className="text-xs font-semibold text-slate-800">{e.title}</div>
          {!compact && e.description && <div className="text-xs text-slate-500">{e.description}</div>}
          <div className="text-[11px] text-slate-400">{e.date ? new Date(e.date).toLocaleDateString("pt-BR") : ""}</div>
        </div>
      ))}
    </div>
  );
}
